import { toast } from 'react-toastify'
import { BaseApiResponse } from '../../types/types'
import { apiSlice } from '../api/apiSlice'
import {
  DashboardReportParams,
  DashboardStatsResponse,
  IPayoutAnalyticsResponse,
  IRevenueAnalyticsResponse,
  ISettlementAnalyticsResponse,
  ISettlementDetailResponse,
  ReportType,
} from './reports.type'
import { getErrorMessage } from '../../helpers'
import { ErrorType } from '../user/user.type'

export const reportsApiSlice = apiSlice.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getDashboardGMVReport: builder.query<
      DashboardStatsResponse,
      DashboardReportParams
    >({
      query: (params) => {
        const { isCustomDateRange, filter, ...filteredParams } = params

        const updatedParams = params.isCustomDateRange
          ? filteredParams
          : { ...filteredParams, filter }

        return {
          url: `v1/dashboard/analytics/gmv`,
          method: 'GET',
          params: updatedParams,
        }
      },
    }),
    getDashboardReport: builder.query<
      DashboardStatsResponse,
      DashboardReportParams
    >({
      query: (params) => {
        const { isCustomDateRange, filter, ...filteredParams } = params

        const updatedParams = params.isCustomDateRange
          ? filteredParams
          : { ...filteredParams, filter }

        return {
          url: `v1/dashboard/summary`,
          method: 'GET',
          params: updatedParams,
        }
      },
    }),
    getSettlementsReport: builder.query<
      ISettlementAnalyticsResponse,
      DashboardReportParams
    >({
      query: (params) => {
        const { isCustomDateRange, filter, ...filteredParams } = params

        const updatedParams = params.isCustomDateRange
          ? filteredParams
          : { ...filteredParams, filter }

        return {
          url: `v1/dashboard/settlements`,
          method: 'GET',
          params: updatedParams,
        }
      },
    }),
    getSettlementsReportByID: builder.query<
      ISettlementDetailResponse,
      { settlementId: string }
    >({
      query: (params) => {
        return {
          url: `v1/dashboard/settlements/${params.settlementId}`,
          method: 'GET',
          params,
        }
      },
    }),
    getRevenueAnalytics: builder.query<
      IRevenueAnalyticsResponse,
      DashboardReportParams
    >({
      query: (params) => {
        const { isCustomDateRange, filter, ...filteredParams } = params

        const updatedParams = params.isCustomDateRange
          ? filteredParams
          : { ...filteredParams, filter }

        return {
          url: `v1/dashboard/analytics/revenue`,
          method: 'GET',
          params: updatedParams,
        }
      },
    }),
    getPayoutAnalytics: builder.query<
      IPayoutAnalyticsResponse,
      DashboardReportParams
    >({
      query: (params) => {
        const { isCustomDateRange, filter, ...filteredParams } = params

        const updatedParams = params.isCustomDateRange
          ? filteredParams
          : { ...filteredParams, filter }

        return {
          url: `v1/dashboard/analytics/payout`,
          method: 'GET',
          params: updatedParams,
        }
      },
    }),
    getCreatorsAnalytics: builder.query<
      {
        data: {
          totalVendors: number
        }
      },
      any
    >({
      query: (params) => {
        const { isCustomDateRange, filter, ...filteredParams } = params

        const updatedParams = params.isCustomDateRange
          ? filteredParams
          : { ...filteredParams, filter }
        return {
          url: `v1/dashboard/analytics/vendors`,
          method: 'GET',
          params: updatedParams,
        }
      },
    }),
    exportStakeholdersReport: builder.mutation<BaseApiResponse, any>({
      query: (params) => {
        return {
          url: `v1/dashboard/export`,
          params,
          method: 'GET',
        }
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data =
            arg.type === 'revenue'
              ? (await queryFulfilled)?.data?.data?.byFulfilmentCentre
              : (await queryFulfilled)?.data?.data
          const reportType = arg.type as ReportType

          let filename

          switch (reportType) {
            case 'settlements':
              filename = 'Settlements Report.csv'
              break
            case 'revenue':
              filename = 'Revenue Report.csv'
              break
            default:
              filename = 'Export.csv'
              break
          }

          if (!data.length) return

          const headers = Object.keys(data[0])
          const rows = data.map((obj: any) =>
            headers
              .map((h) => {
                const val = String(obj[h] ?? '')
                return val.includes(',') ||
                  val.includes('"') ||
                  val.includes('\n')
                  ? `"${val.replace(/"/g, '""')}"`
                  : val
              })
              .join(','),
          )
          const csv = [headers.join(','), ...rows].join('\n')
          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = filename
          a.click()
          URL.revokeObjectURL(url)

          // toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
  }),
})

export const {
  useGetSettlementsReportQuery,
  useGetDashboardReportQuery,
  useExportStakeholdersReportMutation,
  useGetPayoutAnalyticsQuery,
  useGetRevenueAnalyticsQuery,
  useGetSettlementsReportByIDQuery,
  useGetCreatorsAnalyticsQuery,
  useGetDashboardGMVReportQuery,
} = reportsApiSlice
